/**
 * useAudioPlayer Hook
 * US-013: 轉盤轉動音效
 * US-014: 音效同步控制
 *
 * 提供音效載入、播放、同步控制功能
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface AudioOptions {
  volume?: number
  loop?: boolean
  preload?: boolean
}

interface AudioState {
  isLoaded: boolean
  isPlaying: boolean
  isError: boolean
  currentTime: number
  duration: number
}

interface AudioControls {
  play: () => Promise<void>
  pause: () => void
  stop: () => void
  setVolume: (volume: number) => void
  setPlaybackRate: (rate: number) => void
  seek: (time: number) => void
  load: () => Promise<void>
}

export interface UseAudioPlayerReturn {
  audioState: AudioState
  audioControls: AudioControls
}

/**
 * Custom hook for audio management with sync control
 */
export const useAudioPlayer = (
  audioSrc: string,
  options: AudioOptions = {}
): UseAudioPlayerReturn => {
  const { volume = 0.7, loop = false, preload = true } = options

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [audioState, setAudioState] = useState<AudioState>({
    isLoaded: false,
    isPlaying: false,
    isError: false,
    currentTime: 0,
    duration: 0,
  })

  // Initialize audio element
  useEffect(() => {
    if (!audioSrc) return

    const audio = new Audio()
    audio.preload = preload ? 'auto' : 'metadata'
    audio.volume = Math.max(0, Math.min(1, volume))
    audio.loop = loop
    audio.src = audioSrc

    audioRef.current = audio

    // Event listeners for state tracking
    const handleLoadStart = () => {
      setAudioState(prev => ({
        ...prev,
        isLoaded: false,
        isError: false,
      }))
    }

    const handleLoadedData = () => {
      setAudioState(prev => ({
        ...prev,
        isLoaded: true,
        isError: false,
        duration: audio.duration || 0,
      }))
    }

    const handleError = () => {
      console.warn(`Audio loading error for ${audioSrc}`)
      setAudioState(prev => ({
        ...prev,
        isLoaded: false,
        isError: true,
      }))
    }

    const handlePlay = () => {
      setAudioState(prev => ({
        ...prev,
        isPlaying: true,
      }))
    }

    const handlePause = () => {
      setAudioState(prev => ({
        ...prev,
        isPlaying: false,
      }))
    }

    const handleEnded = () => {
      setAudioState(prev => ({
        ...prev,
        isPlaying: false,
        currentTime: 0,
      }))
    }

    const handleTimeUpdate = () => {
      setAudioState(prev => ({
        ...prev,
        currentTime: audio.currentTime || 0,
      }))
    }

    // Add event listeners
    audio.addEventListener('loadstart', handleLoadStart)
    audio.addEventListener('loadeddata', handleLoadedData)
    audio.addEventListener('error', handleError)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('timeupdate', handleTimeUpdate)

    // Start loading if preload enabled
    if (preload) {
      audio.load()
    }

    return () => {
      // Cleanup
      audio.removeEventListener('loadstart', handleLoadStart)
      audio.removeEventListener('loadeddata', handleLoadedData)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('timeupdate', handleTimeUpdate)

      audio.pause()
      audio.src = ''
      audioRef.current = null
    }
  }, [audioSrc, volume, loop, preload])

  // Audio control functions
  const play = useCallback(async (): Promise<void> => {
    const audio = audioRef.current
    if (!audio || audioState.isError) {
      return Promise.reject(new Error('Audio not available or in error state'))
    }

    try {
      // Reset to beginning if ended
      if (audio.ended) {
        audio.currentTime = 0
      }

      const playPromise = audio.play()

      // Handle browsers that return a promise
      if (playPromise !== undefined) {
        await playPromise
      }
    } catch (error) {
      console.warn('Audio play failed:', error)
      // Update error state but don't throw to allow graceful degradation
      setAudioState(prev => ({
        ...prev,
        isError: true,
      }))
    }
  }, [audioState.isError])

  const pause = useCallback(() => {
    const audio = audioRef.current
    if (audio && !audio.paused) {
      audio.pause()
    }
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
  }, [])

  const setVolume = useCallback((newVolume: number) => {
    const audio = audioRef.current
    if (audio) {
      audio.volume = Math.max(0, Math.min(1, newVolume))
    }
  }, [])

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current
    if (audio) {
      // Clamp playback rate to reasonable range
      audio.playbackRate = Math.max(0.25, Math.min(4.0, rate))
    }
  }, [])

  const seek = useCallback(
    (time: number) => {
      const audio = audioRef.current
      if (audio && audioState.isLoaded) {
        audio.currentTime = Math.max(0, Math.min(audio.duration || 0, time))
      }
    },
    [audioState.isLoaded]
  )

  const load = useCallback(async (): Promise<void> => {
    const audio = audioRef.current
    if (!audio) {
      return Promise.reject(new Error('Audio element not initialized'))
    }

    return new Promise((resolve, reject) => {
      const handleLoad = () => {
        audio.removeEventListener('loadeddata', handleLoad)
        audio.removeEventListener('error', handleError)
        resolve()
      }

      const handleError = () => {
        audio.removeEventListener('loadeddata', handleLoad)
        audio.removeEventListener('error', handleError)
        reject(new Error('Audio loading failed'))
      }

      audio.addEventListener('loadeddata', handleLoad)
      audio.addEventListener('error', handleError)
      audio.load()
    })
  }, [])

  // 修復：使用 useMemo 確保 audioControls 物件穩定
  const audioControls: AudioControls = useMemo(
    () => ({
      play,
      pause,
      stop,
      setVolume,
      setPlaybackRate,
      seek,
      load,
    }),
    [play, pause, stop, setVolume, setPlaybackRate, seek, load]
  )

  return {
    audioState,
    audioControls,
  }
}
